/* eslint-disable @typescript-eslint/await-thenable */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import React, { useEffect, useState } from "react";

import getAcces from "@/assets/common/fithub-product-02.webp";
import welcome from "@/assets/common/fithub-products.webp";
import tw, { css } from "twin.macro";
import { useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import { createPhoneCode, validatePhoneCode, changePassword } from "@/services/auth";
import { type AxiosError } from "axios";
import { useDispatch } from "react-redux";
import { userSlice } from "@/store/user";
import Modal from "@/components/Atoms/Modal";
import { StyledWrapperStepsModal } from "@/components/Organisms/AuthStepsModal/styles";
import Login from "@components/Organisms/Login";
import ConfirmPhoneCode from "@components/Organisms/ConfirmPhoneCode";
import SendPhoneCode from "@/components/Organisms/SendPhoneCode";
import PasswordUpdate from "@/components/Organisms/PasswordUpdate";
import AuthStepsImg from "@/components/Molecules/AuthStepsImg";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import useSessionContext from "@/hooks/useSesionContext";
import SignUp from "../SignUp";
import useAppSelector from "@/hooks/useAppSelector";
import useQueryParams from "@/hooks/useQueryParams";
import useGoogleReCaptchaV3 from "@/hooks/useGoogleReCaptchaV3";
import { parseError } from "@/utils/errorHandling";
import useFeatureFlags from "@/hooks/useFeatureFlags";

interface AuthProps {
  onModalClose: (
    event?: React.MouseEvent<HTMLButtonElement, MouseEvent> | React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => void;
  children?: React.ReactNode;
  dismissable: boolean;
  isOpen: boolean;
  isCheckout?: boolean;
  titleLogin?: string;
  onSuccess?: () => void;
}

interface AuthFormFields {
  email: string;
  password: string;
  phoneNumber: string;
  phoneCode: string;
  newPassword: string;
  channel?: "sms" | "whatsapp";
}

export type StepAuth =
  | "login"
  | "signUp"
  | "phoneNumber"
  | "phoneNumberPassword"
  | "phoneCode"
  | "phoneCodePassword"
  | "passwordUpdate";

// FIXME: refactor code
const initialFetchingError = [];
const AuthModal = ({
  onSuccess,
  onModalClose,
  isOpen,
  dismissable,
  titleLogin,
}: AuthProps): React.ReactElement => {
  const isMobile = useMediaQuery("(max-width:{md})");

  const initialStep = useAppSelector((state) => state.generics.modalAuth.initialStep);
  const [step, setStep] = useState<StepAuth>(initialStep ?? "login");
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [tokenUserRegistration, setTokenUserRegistration] = useState("");
  const { isAthenticated } = useSessionContext();

  const isLoggedIn = isAthenticated();
  const { setQueryParams } = useQueryParams();

  const [fetchingError, setFetchingError] = useState<string[] | string>(initialFetchingError);

  const cleanErrors = () => setFetchingError(initialFetchingError);

  const onAuthModalClose = (): void => {
    onModalClose();
    setStep("login");
    setQueryParams({ next: null });
  };
  const { executeReCaptcha } = useGoogleReCaptchaV3({
    shouldLoad: true,
  });
  const { smsOtpEnabled } = useFeatureFlags();

  const updateCurrentUser = (data: Omit<UserAttr, "token">): any =>
    dispatch(
      userSlice.actions.setUser({
        id: data.id,
        firstName: data.first_name,
        lastName: data.last_name,
        email: data.email,
        phoneNumber: data.telephone,
        creditAvaliable: {
          gross: data.credit_available.gross,
          grossLocalized: data.credit_available.gross_localized,
          net: data.credit_available.net,
          netLocalized: data.credit_available.net_localized,
          currency: data.credit_available.currency,
        },
        referralCreditAvailable: data.referral_credit_available
          ? {
              gross: data.referral_credit_available.gross,
              grossLocalized: data.referral_credit_available.gross_localized,
              net: data.referral_credit_available.net,
              netLocalized: data.referral_credit_available.net_localized,
              currency: data.referral_credit_available.currency,
            }
          : null,
        birthday: data.dob,
        gender: data.gender,
        hearAboutUs: data.hear_about_us_source,
        publicId: data.public_id,
        identification: data.identification,
        identificationType: data.identification_type,
        membership_savings: data.membership_savings,
        membership: data.membership,
      })
    );
  const { control, handleSubmit, register, getValues, clearErrors, formState } = useForm<AuthFormFields>({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {},
  });

  const sendCodeMsg = useMutation({
    mutationFn: createPhoneCode,
    mutationKey: ["createPhoneCode"],
    onSuccess: (data) => {
      store?.type === "new_user" ? setStep("phoneCode") : setStep("phoneCodePassword");
    },
    onError: (error: AxiosError<{ telephone: string }>) => {
      setFetchingError(parseError(error));
    },
  });

  const [emailUser, setEmailUser] = useState("");

  const validateCode = useMutation({
    mutationFn: validatePhoneCode,
    mutationKey: ["validatePhoneCode"],
    onSuccess: (data: { email?: string; token?: string }) => {
      if (data?.email) {
        setEmailUser(data.email);
      }
      if (data?.token) {
        setTokenUserRegistration(data.token);
      }
      store?.type === "new_user" ? setStep("signUp") : setStep("passwordUpdate");
    },
    onError: (error: AxiosError<{ telephone: string; code: any }>) => {
      setFetchingError(parseError(error));
    },
  });

  const resetPassword = useMutation({
    mutationFn: changePassword,
    mutationKey: ["changePassword"],
    onSuccess: (data) => {
      setStep("login");
    },
    onError: (error: any) => {
      setFetchingError(parseError(error));
    },
  });

  // Persist form state between submits.
  const [store, setStore] = useState<{ phoneNumber: string; type: string }>();

  const onSubmit = async ({
    password,
    phoneCode,
    phoneNumber,
    email,
    newPassword,
    channel,
  }: AuthFormFields) => {
    try {
      setLoading(true);
      if (step === "phoneCode" && store) {
        await validateCode.mutateAsync({ phoneNumber: store.phoneNumber, code: phoneCode });
      }
      if (step === "phoneCodePassword" && store) {
        await validateCode.mutateAsync({ phoneNumber: store.phoneNumber, code: phoneCode });
      }
      if (step === "phoneNumber") {
        if (channel === "sms" && !smsOtpEnabled) {
          setFetchingError(["El servicio de SMS no está disponible en este momento."]);
          return;
        }
        const resolvedChannel = channel ?? "whatsapp";
        await setStore({ phoneNumber: `+${phoneNumber}`, type: "new_user" });
        const recaptchaToken = await executeReCaptcha("request_new_user");
        await sendCodeMsg.mutateAsync({
          phoneNumber: `+${phoneNumber}`,
          type: "new_user",
          channel: resolvedChannel,
          recaptchaToken,
        });
      }
      if (step === "phoneNumberPassword") {
        if (channel === "sms" && !smsOtpEnabled) {
          setFetchingError(["El servicio de SMS no está disponible en este momento."]);
          return;
        }
        const resolvedChannel = channel ?? "whatsapp";
        await setStore({ phoneNumber: `+${phoneNumber}`, type: "change_password" });
        const recaptchaToken = await executeReCaptcha("request_change_password");
        await sendCodeMsg.mutateAsync({
          phoneNumber: `+${phoneNumber}`,
          type: "change_password",
          channel: resolvedChannel,
          recaptchaToken,
        });
      }
      if (step === "passwordUpdate") {
        const recaptchaToken = await executeReCaptcha("reset_password");
        await resetPassword.mutateAsync({ newPassword, token: validateCode?.data?.token, recaptchaToken });
      }
    } finally {
      setLoading(false);
    }
  };

  // TODO: Mejorar esto mas adelante
  const triggerSubmit = async (extraData: Record<string, any> = {}) =>
    await onSubmit({ ...extraData, ...getValues() });

  const getStep: Record<string, React.ReactElement> = {
    login: (
      <Login
        setStep={setStep}
        onClose={onAuthModalClose}
        isMobile={isMobile}
        updateCurrentUser={updateCurrentUser}
        onSuccess={onSuccess}
      />
    ),
    phoneCode: (
      <ConfirmPhoneCode
        setStep={setStep}
        phoneNumber={store?.phoneNumber ?? ""}
        controller={{ control, formState, errors: formState.errors }}
        loading={loading}
        type="signUp"
        fetchingError={fetchingError}
        cleanErrors={cleanErrors}
        onClose={onAuthModalClose}
        isMobile={isMobile}
      />
    ),
    phoneCodePassword: (
      <ConfirmPhoneCode
        setStep={setStep}
        phoneNumber={store?.phoneNumber ?? ""}
        controller={{ control, formState, errors: formState.errors }}
        loading={loading}
        type="recovery"
        fetchingError={fetchingError}
        cleanErrors={cleanErrors}
        onClose={onAuthModalClose}
        isMobile={isMobile}
      />
    ),
    phoneNumber: (
      <SendPhoneCode
        onClose={onAuthModalClose}
        controller={{ clearErrors, control, formState }}
        setStep={setStep}
        type="signUp"
        onSubmit={triggerSubmit}
        loading={loading}
        fetchingError={fetchingError}
        cleanErrors={cleanErrors}
        isMobile={isMobile}
      />
    ),
    phoneNumberPassword: (
      <SendPhoneCode
        onClose={onAuthModalClose}
        controller={{ clearErrors, control, formState }}
        setStep={setStep}
        type="recovery"
        onSubmit={triggerSubmit}
        loading={loading}
        fetchingError={fetchingError}
        cleanErrors={cleanErrors}
        isMobile={isMobile}
      />
    ),

    signUp: (
      <SignUp
        onClose={onAuthModalClose}
        isMobile={isMobile}
        updateCurrentUser={updateCurrentUser}
        setStep={setStep}
        tokenUserRegistration={tokenUserRegistration}
      />
    ),
    passwordUpdate: (
      <PasswordUpdate
        email={emailUser}
        controller={{ register }}
        loading={loading}
        onClose={onAuthModalClose}
        fetchingError={fetchingError}
        cleanErrors={cleanErrors}
        isMobile={isMobile}
        setStep={setStep}
      />
    ),
  };

  useEffect(() => {
    cleanErrors();
  }, [step]);

  return (
    <Modal
      isOpen={isOpen && !isLoggedIn}
      // dismissable={dismissable}
      handleClose={onAuthModalClose}
      classes={{
        backdrop: css`
          ${tw`overflow-auto z-[52]`}
          ${isMobile ? tw`block` : tw`flex`}
        `,
        card: css`
          ${tw`p-0`}
          ${!isMobile ? tw`rounded-xl` : tw`rounded-none w-full  `}
        `,
      }}
    >
      <StyledWrapperStepsModal>
        {!isMobile && (
          <AuthStepsImg
            src={step === "login" || step === "signUp" ? welcome : getAcces}
            alt="Man typing on his phone"
          />
        )}
        {(step === "signUp" || step === "login") && (
          <div
            css={
              isMobile
                ? [
                    css`
                      min-height: 100%;
                      ${tw`flex-1 w-full [height:100dvh] overflow-y-auto`}
                    `,
                  ]
                : [tw`w-1/2 `]
            }
          >
            {getStep[step]}
          </div>
        )}

        {step !== "signUp" && step !== "login" && (
          <form
            onSubmit={handleSubmit(onSubmit)}
            css={
              isMobile
                ? [
                    css`
                      min-height: 100%;
                      ${tw`flex-1 w-full [height:100dvh] overflow-y-auto`}
                    `,
                  ]
                : [tw`w-1/2 `]
            }
          >
            {getStep[step]}
          </form>
        )}
      </StyledWrapperStepsModal>
    </Modal>
  );
};
export default AuthModal;
